import type { IconName } from '@cencosud-cencommerce/paris-uikit';

import { INTANGIBLE_KEY } from '@/constants';
import type {
  Cart,
  DeliveryGroup as CartDeliveryGroup,
  OptionSelected
} from '@/modules/cart/domain/Cart';
import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';
import {
  ShippingMethodEnum,
  ShippingSubTypeEnum,
  ShippingTypeEnum
} from '@/modules/product/domain/enumsTypes.ts';
import {
  formatPriceString,
  priceDtoToPriceString
} from '@/modules/shared/mappers/priceDtoToPriceString';
import type {
  DeliveryProduct,
  DeliveryProductService,
  DeliveryProductSpec,
  PickupOption,
  ShippingTimeSlot
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type {
  DeliveryGroup,
  ShippingMethod,
  ShippingOption
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type {
  DeliveryGroupDto,
  DeliveryItemDto,
  GeneralCollectOptionDto,
  GeneralCollectShippingOptionDto,
  GeneralDeliveryOptionDto,
  GeneralDeliveryShippingOptionDto,
  HttpShippingMethodDraft,
  HttpShippingMethodDto,
  TimeSlotDto
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { ECollectNodeAdmin } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import type { ShippingMethodLineItem } from '@/Pages/ShippingPage/ShippingPageContext';
import METHODS_ORDER from '@/sections/shippingMethods/constants/methodsOrder';
import {
  BRAND_COPEC,
  BRAND_EASY,
  BRAND_JUMBO,
  BRAND_PARIS,
  BRAND_SANTA_ISABEL,
  DEFAULT_ORIGIN_NODE_TYPE,
  DEFAULT_SHIPPING_SUB_TYPE,
  ICON_PARIS,
  ICON_PRODUCT,
  ICON_STORE,
  LABEL_CHANGE_COPEC,
  LABEL_CHANGE_STORE,
  LABEL_DELIVERY_HOURS_AM_TO_PM,
  LABEL_DELIVERY_HOURS_PM,
  LABEL_PICKUP_FROM,
  LABEL_RECEIVE_BETWEEN,
  LABEL_RECEIVE_ON,
  LABEL_RECEIVE_TODAY,
  LABEL_RECEIVE_TOMORROW,
  LABEL_SOLD_BY,
  LABEL_TIME_SLOT_QUESTION
} from '@/sections/shippingMethods/mappers/constants';

type SelectedShippingOption = {
  selectedShippingOption:
    | GeneralDeliveryShippingOptionDto
    | GeneralCollectShippingOptionDto;
  scheduledSelection?: GeneralDeliveryOptionDto;
};

const getServiceType = (addonName: string): DeliveryProductService['type'] => {
  if (
    addonName.toLowerCase().includes('instalacion') ||
    addonName.toLowerCase().includes('instalación')
  ) {
    return 'installation';
  } else if (addonName.toLowerCase().includes('armado')) {
    return 'assembly';
  }
  return 'recicambio';
};

export const productOptionToService = (
  productOption: CartProductOption
): DeliveryProductService => {
  const serviceType =
    productOption.addonType === 'warranty'
      ? productOption.addonType
      : getServiceType(productOption.name);
  let amount = `${productOption.quantity}`;
  const years = productOption.warrantyTime
    ? productOption.warrantyTime / 12
    : 0;
  if (years > 0) {
    amount += `| ${years} año${years > 1 ? 's' : ''}`;
  }

  return {
    title: productOption.name,
    amount,
    type: serviceType
  };
};

export const deliveryItemToDeliveryProduct = (
  item: DeliveryItemDto,
  cart: Cart
): DeliveryProduct => {
  const services = cart.productOptions
    .filter((productOption) => productOption.productSku === item.itemId)
    .map(productOptionToService);

  const lineItem = cart.lineItems.find(
    (lineItem) => lineItem.variant.sku == item.itemId
  );

  const imageUrl = lineItem?.variant.images[0].url;
  const color = lineItem?.variant.attributes?.find(
    (attr) => attr.name === 'color'
  )?.value;
  const size = lineItem?.variant.attributes?.find(
    (attr) => attr.name === 'size'
  )?.value;

  const specs: DeliveryProductSpec[] = [
    color
      ? ({ label: 'Color', value: color } as DeliveryProductSpec)
      : undefined,
    size ? ({ label: 'Tamaño', value: size } as DeliveryProductSpec) : undefined
  ].flatMap((spec) => (spec ? [spec] : []));

  /* TODO: COM2-5220 */
  // const samples = [];

  /* TODO: COM2-4722 */
  // const services = [];
  return {
    productId: lineItem?.id ?? '',
    quantity: item.quantity.value,
    productSKU: item.itemId,
    name: lineItem?.name ?? '',
    imageUrl: imageUrl ?? '',
    saleTagProps: {
      storeName: item.sellerId,
      label: LABEL_SOLD_BY
    },
    isPreSale: Boolean(lineItem?.variant?.isPreSale),
    specs,
    services,
    onDelete: (id: string) => {
      console.log('remove: ', id);
    }
  };
};

export const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-CL', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC'
  });
};

export const formatPrice = (price: string): string | undefined => {
  if (price && price !== '0') {
    return priceDtoToPriceString({
      currencyCode: 'CLP',
      centAmount: parseInt(price)
    });
  }
  return undefined;
};

export const findCartDeliveryGroup = (
  cart: Cart,
  deliveryGroup: DeliveryGroupDto
) => {
  return cart.deliveryInfo?.deliveryGroups.find((group) => {
    const cartItems: string[] = group.products;
    const groupItems = deliveryGroup.items.map((group) => group.itemId);
    return cartItems.some((itemId) => groupItems.includes(itemId));
  });
};

export const getSelectedShippingOption = (
  cart: Cart,
  deliveryGroup: DeliveryGroupDto,
  isInitialLoad?: boolean
): SelectedShippingOption | undefined => {
  let selectedShippingOption;
  let scheduledSelection;

  if (isInitialLoad) {
    selectedShippingOption = deliveryGroup.shippingOptions.find(
      (option) => option.selected || option.shippingType === 'DELIVERY'
    );
    scheduledSelection = selectedShippingOption?.options[0];
  } else {
    const cartDeliveryGroup = findCartDeliveryGroup(cart, deliveryGroup);
    if (cartDeliveryGroup && cartDeliveryGroup.shippingOption) {
      const { shippingOption } = cartDeliveryGroup;
      selectedShippingOption = deliveryGroup.shippingOptions.find((opt) => {
        if (
          shippingOption.shippingType === 'DELIVERY' &&
          shippingOption.shippingMethod === ShippingMethodEnum.SCHEDULED &&
          cartDeliveryGroup.optionSelected
        ) {
          const { optionSelected } = cartDeliveryGroup;
          scheduledSelection = opt.options.find(
            (option) => option.shipDate === optionSelected.shipDate
          );
          return (
            opt.shippingMethod === shippingOption.shippingMethod &&
            opt.shippingType === shippingOption.shippingType &&
            opt.shippingSubType === shippingOption.shippingSubType &&
            scheduledSelection
          );
        }
        return (
          opt.shippingMethod === shippingOption.shippingMethod &&
          opt.shippingType === shippingOption.shippingType &&
          opt.shippingSubType === shippingOption.shippingSubType
        );
      });
    }
  }

  return selectedShippingOption
    ? { selectedShippingOption, scheduledSelection }
    : undefined;
};

export const generateDateLabel = (
  option: GeneralDeliveryShippingOptionDto,
  scheduledSelected: number = 0
) => {
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  const tomorrow = new Date();
  tomorrow.setHours(12, 0, 0, 0);
  tomorrow.setDate(today.getDate() + 1);

  const formattedToday = formatDate(today);
  const formattedTomorrow = formatDate(tomorrow);

  if (option.options.length > 0) {
    const firstScheduled = option?.options[0].deliveryDateFrom;
    const firstScheduleDate = firstScheduled
      ? new Date(firstScheduled)
      : undefined;

    if (firstScheduleDate) {
      firstScheduleDate.setHours(12, 0, 0, 0);
    }

    const firstScheduledTo = option?.options[0].deliveryDateTo;
    const firstScheduledToDate = firstScheduledTo
      ? new Date(firstScheduledTo)
      : undefined;

    if (firstScheduledToDate) {
      firstScheduledToDate.setHours(12, 0, 0, 0);
    }

    const rangeDayFrom = firstScheduleDate?.toLocaleString('es-es', {
      day: 'numeric'
    });
    const rangeDayTo = firstScheduledToDate?.toLocaleString('es-es', {
      day: 'numeric',
      month: 'long'
    });
    const rangeDateValue =
      firstScheduleDate && firstScheduledToDate
        ? firstScheduleDate.getMonth() === firstScheduledToDate.getMonth()
          ? `${rangeDayFrom} y ${rangeDayTo}`
          : `${rangeDayFrom} de ${firstScheduleDate.toLocaleString('es-es', { month: 'long' })} y ${rangeDayTo}`
        : undefined;

    const scheduledDate = new Date(
      option?.options[scheduledSelected].deliveryDateFrom
    );
    scheduledDate.setHours(12, 0, 0, 0);
    const scheduledDateValue = formatDate(scheduledDate);

    return {
      [ShippingMethodEnum.SAME_DAY]: {
        label: LABEL_RECEIVE_TODAY,
        value: formattedToday
      },
      [ShippingMethodEnum.NEXT_DAY]: {
        label: LABEL_RECEIVE_TOMORROW,
        value: formattedTomorrow
      },
      [ShippingMethodEnum.SCHEDULED]: {
        label: LABEL_RECEIVE_ON,
        value: scheduledDateValue
      },
      [ShippingMethodEnum.DATE_RANGE]: {
        label: LABEL_RECEIVE_BETWEEN,
        value: rangeDateValue
      },
      [ShippingMethodEnum.SCHEDULED_DEFERRED]: {
        label: '',
        value: ''
      }
    };
  }
};

export const generateChips = (
  option: GeneralDeliveryShippingOptionDto
): { chips: [] | ShippingTimeSlot[]; description: string } => {
  const { shippingMethod, options } = option;
  const timeSlots = options.flatMap((option) => option.timeSlots);
  const nonTHTimeSlots = timeSlots.filter((slot) => slot?.name !== 'TH');
  const THSlot = timeSlots?.find((slot) => slot?.name == 'TH');

  const defaultObject = {
    chips: [],
    description: `Entre ${THSlot?.deliveryTimeStart} y ${THSlot?.deliveryTimeEnd}`
  };

  if (
    shippingMethod === ShippingMethodEnum.NEXT_DAY ||
    shippingMethod === ShippingMethodEnum.SAME_DAY
  ) {
    if (nonTHTimeSlots.length > 0) {
      return {
        /* TODO: instead looping options, need to loop option[0].timeSlots when contract is changed */
        chips: options
          .map((opt) => {
            if (opt.timeSlots) {
              const { timeSlots } = opt;
              return {
                label: `${timeSlots[0].deliveryTimeStart}-${timeSlots[0].deliveryTimeEnd}`,
                value: {
                  shipDate: opt.shipDate,
                  timeSlot: opt.timeSlots[0]
                },
                disabled: false,
                selected: false
              } as ShippingTimeSlot;
            }
            return undefined;
          })
          .filter((chip) => chip !== undefined),
        description: LABEL_TIME_SLOT_QUESTION
      };
    } else {
      if (shippingMethod === ShippingMethodEnum.NEXT_DAY) {
        return {
          chips: [],
          description: LABEL_DELIVERY_HOURS_AM_TO_PM
        };
      }

      if (shippingMethod === ShippingMethodEnum.SAME_DAY) {
        return {
          chips: [],
          description: LABEL_DELIVERY_HOURS_PM
        };
      }
      return defaultObject;
    }
  }

  if (shippingMethod === ShippingMethodEnum.SCHEDULED) {
    return {
      chips: [],
      description: LABEL_DELIVERY_HOURS_AM_TO_PM
    };
  }

  if (shippingMethod === ShippingMethodEnum.DATE_RANGE) {
    return {
      chips: [],
      description: LABEL_DELIVERY_HOURS_AM_TO_PM
    };
  }

  return defaultObject;
};

const isActiveOption = (
  cartDeliveryGroup?: CartDeliveryGroup,
  option?: GeneralDeliveryShippingOptionDto
) => {
  if (!cartDeliveryGroup || !option) return false;
  const current = cartDeliveryGroup?.shippingOption;
  return (
    current?.shippingType === option?.shippingType &&
    current?.shippingSubType === option?.shippingSubType &&
    current?.shippingMethod === option?.shippingMethod
  );
};

export const shippingOptionsDtoToShippingOption = (
  options: GeneralDeliveryShippingOptionDto[],
  cart: Cart,
  groupDto: DeliveryGroupDto,
  completeSkuHistory: ShippingMethodLineItem[]
): ShippingOption[] | undefined => {
  const shippingOptions: ShippingOption[] = [];
  const cartDeliveryGroup = cart?.deliveryInfo?.deliveryGroups?.find(
    (group) => group?.id === groupDto?.id
  );

  options?.sort((a, b) => {
    return METHODS_ORDER[a.shippingMethod] - METHODS_ORDER[b.shippingMethod];
  });

  options?.forEach(
    (option: GeneralDeliveryShippingOptionDto, index: number) => {
      const isScheduled =
        option?.shippingMethod === ShippingMethodEnum.SCHEDULED;
      const product = groupDto?.items?.[0]?.itemId;
      const deliveryInfo = completeSkuHistory?.find(
        (skus) => skus?.sku === product
      )?.clientPreference?.[0];

      const isCustomSelection =
        isScheduled && deliveryInfo && deliveryInfo?.deliveryDateFrom;

      const customSelection = isCustomSelection
        ? option?.options?.find((opt) =>
            opt?.deliveryDateFrom?.startsWith(
              deliveryInfo?.deliveryDateFrom as string
            )
          )
        : undefined;
      const indexSelected = customSelection
        ? option?.options?.indexOf(customSelection)
        : 0;

      const dateLabel = generateDateLabel(option, indexSelected);
      const isOptionSelected = option?.selected;
      const shippingMethod = option?.shippingMethod;
      const isDeliverySchedule =
        option?.shippingType === ShippingTypeEnum.DELIVERY && isScheduled;
      const isNotSchedule = shippingMethod !== ShippingMethodEnum.SCHEDULED;
      const isScheduledAndSelected = isDeliverySchedule && isOptionSelected;
      const onlyScheduleAvailable = options?.length === 1 && isDeliverySchedule;
      const hasOneOption = option?.options?.length === 1;
      const isScheduledWithOneOption = isDeliverySchedule && hasOneOption;

      const shouldPushOption =
        isNotSchedule ||
        isScheduledAndSelected ||
        onlyScheduleAvailable ||
        isScheduledWithOneOption;

      const isOptionAppliedToCart = isActiveOption(cartDeliveryGroup, option);

      if (shouldPushOption) {
        let price;
        let crossedOutPrice;

        if (cartDeliveryGroup && isOptionAppliedToCart) {
          const proratedCost = cartDeliveryGroup?.proratedCost;
          const proratedAmount = proratedCost?.centAmount;

          price = proratedAmount
            ? priceDtoToPriceString(proratedCost)
            : undefined;

          const shipmentCostTotal =
            cartDeliveryGroup?.shippingOption?.shipmentCostTotal;

          crossedOutPrice =
            shipmentCostTotal && +shipmentCostTotal !== proratedAmount
              ? priceDtoToPriceString({
                  currencyCode: 'CLP',
                  centAmount: parseInt(String(shipmentCostTotal))
                })
              : undefined;
        } else {
          price = formatPrice(option?.shipmentCostTotal ?? '0');
        }

        const { chips, description } = generateChips(option);

        shippingOptions.push({
          id: `delivery-${groupDto?.id}-${index}`,
          selected: isOptionSelected,
          label: dateLabel ? dateLabel[option?.shippingMethod]?.label : '',
          date: dateLabel ? dateLabel[option?.shippingMethod]?.value || '' : '',
          description,
          shipDate: customSelection?.shipDate ?? option?.options?.[0]?.shipDate,
          deliveryDateFrom:
            customSelection?.deliveryDateFrom ??
            option?.options?.[0]?.deliveryDateFrom,
          deliveryDateTo:
            customSelection?.deliveryDateTo ??
            option?.options?.[0]?.deliveryDateTo,
          logisticsModel:
            cartDeliveryGroup?.shippingOption?.logisticsModel ??
            option?.logisticsModel,
          originNodeAdmin: option?.originNodeAdmin,
          originNodeCode: option?.originNodeCode ?? '',
          originNodeType: option?.originNodeType ?? DEFAULT_ORIGIN_NODE_TYPE, // undefined not possible?
          shippingType: option?.shippingType,
          shippingSubType: option?.shippingSubType ?? DEFAULT_SHIPPING_SUB_TYPE, // undefined not possible?
          shippingMethod: option?.shippingMethod,
          timeSlot: (customSelection?.timeSlots ??
            option?.options?.[0]?.timeSlots?.[0]) as TimeSlotDto,
          price,
          crossedOutPrice,
          isFlash: option?.shippingMethod === ShippingMethodEnum.SAME_DAY,
          onChange: () => {},
          chips,
          shippingCapability: option?.shippingCapability
        });
      }
    }
  );

  return shippingOptions;
};

export const getBrand = (
  collectNodeAdmin: ECollectNodeAdmin | string | undefined
): PickupOption['brand'] => {
  if (collectNodeAdmin === ECollectNodeAdmin.JUMBO) return BRAND_JUMBO;
  if (collectNodeAdmin === ECollectNodeAdmin.EASY) return BRAND_EASY;
  if (collectNodeAdmin === ECollectNodeAdmin.COPEC) return BRAND_COPEC;
  if (collectNodeAdmin === ECollectNodeAdmin.SANTA_ISABEL)
    return BRAND_SANTA_ISABEL;
  return BRAND_PARIS;
};

export const getDateLabel = (isoStringDate: string) => {
  const date = new Date(isoStringDate);
  date.setHours(12, 0, 0, 0);

  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long'
  }).format(date);
};

export const getDefaultOption = (
  options: GeneralCollectOptionDto[],
  commune: string
) => {
  let isCommuneOption = true;
  const defaultOptions = options.filter(
    (option) => option.collectNodeAddress?.collectComunaCode === commune
  );

  let defaultOption =
    defaultOptions.find(
      (option) =>
        option.collectNodeName?.toLowerCase().includes('paris') ||
        option.collectNodeName?.toLowerCase().includes('parís')
    ) || defaultOptions[0];

  if (!defaultOption) {
    isCommuneOption = false;
    defaultOption = options.reduce((best, current) => {
      if (current.rank < best.rank) {
        return current;
      }
      return best;
    });
  }

  if (options?.length === 1) {
    isCommuneOption = true;
    defaultOption = options?.[0];
  }

  const defaultOptionIndex = options.indexOf(defaultOption);
  return { defaultOption, isCommuneOption, defaultOptionIndex };
};

const getPickUpAddress = (option: GeneralCollectOptionDto) => {
  const collectOption = option?.collectNodeAddress;
  if (!collectOption) return '';
  return `${collectOption?.collectNodeAddress1} ${collectOption?.collectNodeAddress2}, ${collectOption?.collectComunaName}`;
};

export const collectShippingOptionDtoToPickUp = (
  dto: GeneralCollectShippingOptionDto,
  cart: Cart,
  groupId: string
): PickupOption => {
  const { defaultOption, isCommuneOption } = getDefaultOption(
    dto.options,
    cart?.shippingAddress?.commune ?? ''
  );
  const cartDeliveryGroup = cart.deliveryInfo?.deliveryGroups.find(
    (group) => group.id === groupId
  );

  const optionSelected: OptionSelected | undefined =
    cartDeliveryGroup?.optionSelected;
  const collectNodeAdmin = optionSelected?.collectNodeAdmin ?? '';
  const collectNodeName = optionSelected?.collectNodeName ?? '';

  const defaultCollectNodeAdmin = defaultOption?.collectNodeAdmin ?? '';
  const defaultCollectNodeCode = defaultOption?.collectNodeCode ?? '';
  const defaultCollectNodeName = defaultOption?.collectNodeName ?? '';

  const isOptionAppliedToCart =
    defaultCollectNodeAdmin === collectNodeAdmin && dto.selected;

  const useProrratedCost =
    cartDeliveryGroup &&
    isOptionAppliedToCart &&
    cartDeliveryGroup.proratedCost;

  const shipmentCostTotal = Number(
    cartDeliveryGroup?.shippingOption?.shipmentCostTotal
  );

  const crossedOutPrice =
    useProrratedCost &&
    shipmentCostTotal !== cartDeliveryGroup.proratedCost.centAmount
      ? priceDtoToPriceString({
          currencyCode: 'CLP',
          centAmount: parseInt(
            String(cartDeliveryGroup.shippingOption.shipmentCostTotal)
          )
        })
      : undefined;

  let price = formatPrice(dto?.shipmentCostTotal);

  if (useProrratedCost) {
    price = priceDtoToPriceString(cartDeliveryGroup.proratedCost);
  } else if (cartDeliveryGroup?.totalPrice) {
    const shipmentCost = Number(dto?.shipmentCostTotal ?? 0);
    price = !isNaN(shipmentCost)
      ? formatPriceString(shipmentCost)
      : formatPriceString(0);
  }

  const title = isOptionAppliedToCart
    ? (collectNodeName ?? defaultCollectNodeName)
    : defaultCollectNodeName;
  const brand = getBrand(defaultCollectNodeAdmin ?? '');
  const defaultOptionDate = new Date(defaultOption?.deliveryDateFrom);
  defaultOptionDate.setHours(12, 0, 0, 0);
  const dateLabel = formatDate(defaultOptionDate) ?? '';
  const pickUpAddress = getPickUpAddress(defaultOption);
  const isStore = dto.shippingSubType === ShippingSubTypeEnum.IN_STORE;
  const iconName: IconName = isStore ? ICON_PARIS : ICON_STORE;
  const ctaLabel = isStore ? LABEL_CHANGE_STORE : LABEL_CHANGE_COPEC;
  const isFree = !price || price === '0' || price === '$0';

  return {
    id: defaultCollectNodeCode,
    title: title,
    brand,
    selected: dto.selected,
    cta: {
      iconName,
      label: ctaLabel,
      onClick: () => {}
    },
    radioButton: {
      id: defaultCollectNodeCode,
      name: defaultOption?.collectNodeName ?? '',
      value: defaultCollectNodeCode
    },
    deliveryDateFrom: defaultOption?.deliveryDateFrom ?? '',
    deliveryDateTo: defaultOption?.deliveryDateTo ?? '',
    shippingType: dto?.shippingType ?? '',
    shippingSubType: dto?.shippingSubType ?? '',
    shippingMethod: dto?.shippingMethod ?? '',
    collectNodeCode: defaultOption?.collectNodeCode ?? '',
    originNodeCode: defaultOption?.originNodeCode ?? '',
    originNodeType: defaultOption?.originNodeType ?? '',
    shipDate: defaultOption?.shipDate ?? '',
    hasCommuneOption: isCommuneOption,
    description1Label: LABEL_PICKUP_FROM,
    description1Value: dateLabel,
    description2: pickUpAddress,
    isFree: isFree,
    crossedOutPrice,
    price,
    showBorder: false
  } as PickupOption;
};

export const shippingMethodDtoToShippingMethod = (
  dto: HttpShippingMethodDto,
  cart: Cart,
  completeSkuHistory: ShippingMethodLineItem[]
): ShippingMethod => {
  const tangibleDeliveryGroups: DeliveryGroupDto[] =
    dto?.deliveryGroups?.filter(
      (group) =>
        !group?.items?.some((item) =>
          cart?.lineItems?.some(
            (lineItem) =>
              lineItem?.variant?.sku === item?.itemId &&
              lineItem?.productType?.key === INTANGIBLE_KEY
          )
        )
    );

  const intangibleDeliveryGroups: DeliveryGroupDto[] =
    dto?.deliveryGroups?.filter((group) =>
      group?.items?.some((item) =>
        cart?.lineItems?.some(
          (lineItem) =>
            lineItem?.variant?.sku === item?.itemId &&
            lineItem?.productType?.key === INTANGIBLE_KEY
        )
      )
    );

  const totalGroups =
    tangibleDeliveryGroups?.length + intangibleDeliveryGroups?.length;

  const tangibles: DeliveryGroup[] = tangibleDeliveryGroups?.map(
    (group: DeliveryGroupDto, index) => {
      const shippingDeliveryOptions = group?.shippingOptions?.filter(
        (opt) => opt?.shippingType === ShippingTypeEnum.DELIVERY
      );

      const pickupOptions = group?.shippingOptions
        ?.filter((option) => option.shippingType === ShippingTypeEnum.COLLECT)
        ?.map((collectOption) =>
          collectShippingOptionDtoToPickUp(
            collectOption as GeneralCollectShippingOptionDto,
            cart,
            group.id
          )
        );

      const title =
        totalGroups === 1
          ? 'Entrega'
          : `Entrega ${index + 1} de ${totalGroups}`;

      return {
        id: group?.id,
        title,
        iconName: ICON_PRODUCT,
        sellerType: group?.classificationGroup?.slice(0, 2),
        /* TODO: there is no attr found for this */
        // saleTagLabel: 'Enviada por',
        // saleTagStoreName: group.items[0].sellerId,
        intangible: false,
        productList: group?.items?.map((item) =>
          deliveryItemToDeliveryProduct(item, cart)
        ),
        pickupOptions,
        shippingOptions: shippingOptionsDtoToShippingOption(
          shippingDeliveryOptions,
          cart,
          group,
          completeSkuHistory
        ),
        scheduledOptions: shippingDeliveryOptions?.filter(
          (deliveryOption) =>
            deliveryOption?.shippingMethod === ShippingMethodEnum.SCHEDULED
        ),
        classificationGroupMeta: group?.classificationGroupMeta
      };
    }
  );

  const intangibles: DeliveryGroup[] = intangibleDeliveryGroups.map(
    (group, index) => {
      const title =
        totalGroups === 1
          ? 'Entrega'
          : `Entrega ${tangibleDeliveryGroups.length + 1 + index} de ${totalGroups}`;
      return {
        id: group?.id,
        title,
        iconName: ICON_PRODUCT,
        intangible: true,
        productList: group?.items?.map((item) =>
          deliveryItemToDeliveryProduct(item, cart)
        )
      };
    }
  );

  return {
    deliveryGroups: [...tangibles, ...intangibles]
  };
};

export const getOtherGroups = (
  results: HttpShippingMethodDto,
  shippingMethods: ShippingMethod,
  deliveryId: string,
  cart: Cart
) => {
  const otherGroups = results?.deliveryGroups?.filter(
    (group) => group?.id !== deliveryId
  );

  const selectedOptions: HttpShippingMethodDraft[] = [];

  otherGroups?.forEach((group) => {
    const shippingMethodGroup = shippingMethods?.deliveryGroups?.find(
      (shippingGroup) => shippingGroup?.id === group?.id
    );
    if (cart && !shippingMethodGroup?.intangible) {
      const cartDeliveryGroup = findCartDeliveryGroup(cart, group);

      const shippingOptionSelected = group?.shippingOptions?.find(
        (option) =>
          option?.shippingType ===
            cartDeliveryGroup?.shippingOption?.shippingType &&
          option?.shippingSubType ===
            cartDeliveryGroup?.shippingOption?.shippingSubType &&
          option?.shippingMethod ===
            cartDeliveryGroup?.shippingOption?.shippingMethod
      );

      const optionSelected = shippingOptionSelected?.options?.[0];

      if (cartDeliveryGroup && optionSelected) {
        selectedOptions.push({
          deliveryGroupId: group.id,
          shippingType: cartDeliveryGroup?.shippingOption?.shippingType,
          shippingSubType: cartDeliveryGroup?.shippingOption?.shippingSubType,
          shippingMethod: cartDeliveryGroup?.shippingOption?.shippingMethod,
          option: {
            originNodeCode: cartDeliveryGroup?.optionSelected
              .originNodeCode as string,
            originNodeType: cartDeliveryGroup?.optionSelected
              ?.originNodeType as HttpShippingMethodDraft['option']['originNodeType'],
            shipDate: optionSelected?.shipDate,
            collectNodeCode:
              cartDeliveryGroup?.shippingOption?.shippingType === 'COLLECT'
                ? cartDeliveryGroup?.optionSelected?.collectNodeCode
                : undefined,
            timeSlot:
              cartDeliveryGroup?.shippingOption?.shippingType === 'DELIVERY'
                ? optionSelected?.timeSlots?.[0]
                : undefined
          }
        });
      } else {
        const defaultOption = group?.shippingOptions?.find(
          (option) => option?.selected
        );
        selectedOptions.push({
          deliveryGroupId: group?.id,
          shippingType:
            defaultOption?.shippingType as HttpShippingMethodDraft['shippingType'],
          shippingSubType: defaultOption?.shippingSubType,
          shippingMethod: defaultOption?.shippingMethod,
          option: {
            originNodeCode: defaultOption?.originNodeCode,
            originNodeType: defaultOption?.originNodeType,
            shipDate: defaultOption?.options?.[0]?.shipDate as string,
            timeSlot:
              defaultOption?.shippingType === 'DELIVERY'
                ? defaultOption?.options?.[0]?.timeSlots?.[0]
                : undefined,
            collectNodeCode:
              defaultOption?.shippingType === 'COLLECT'
                ? defaultOption?.options?.[0]?.collectNodeCode
                : undefined
          }
        });
      }
    }
  });

  return selectedOptions;
};
